import React, { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Sparkles, FileText, Download, Loader2, AlertTriangle, CheckCircle, RefreshCw, Wand2 } from "lucide-react";
import { apiClient } from "../services/api";

const PresetCard = ({ preset, selected, onSelect }) => (
  <button
    onClick={() => onSelect(preset.id)}
    className={`text-left p-4 rounded-xl border transition-all ${
      selected ? "border-primary bg-primary/5 ring-2 ring-primary/20" : "border-border bg-card hover:border-primary/50"
    }`}
  >
    <div className="flex items-start justify-between gap-2 mb-2">
      <div>
        <p className="font-semibold text-sm">{preset.label}</p>
        <p className="text-xs text-muted-foreground mt-0.5">{preset.country} · {preset.currency}</p>
      </div>
      {selected && <CheckCircle className="h-4 w-4 text-primary flex-shrink-0" />}
    </div>
    <p className="text-[11px] text-muted-foreground italic">{preset.legal_basis}</p>
  </button>
);

export default function AIContractGenerator() {
  const { t, i18n } = useTranslation();
  const [presets, setPresets] = useState([]);
  const [presetId, setPresetId] = useState("");
  const [employees, setEmployees] = useState([]);
  const [employeeId, setEmployeeId] = useState("");
  const [salary, setSalary] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [instructions, setInstructions] = useState("");
  const [generating, setGenerating] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    apiClient.get("v1/contracts/ai/presets").then((r) => {
      setPresets(r.data || []);
      if (r.data?.length) setPresetId(r.data[0].id);
    }).catch(() => {});
    apiClient.get("v1/employees/?limit=200").then((r) => {
      setEmployees(r.data?.employees || r.data?.items || []);
    }).catch(() => {});
  }, []);

  const currentPreset = useMemo(
    () => presets.find((p) => p.id === presetId),
    [presets, presetId]
  );

  const generate = async () => {
    if (!presetId) return;
    setGenerating(true);
    setError(null);
    setResult(null);
    try {
      const r = await apiClient.post("v1/contracts/ai/generate", {
        preset_id: presetId,
        employee_id: employeeId ? parseInt(employeeId) : null,
        salary: salary ? parseFloat(salary) : null,
        start_date: startDate || null,
        end_date: endDate || null,
        custom_instructions: instructions || null,
      });
      setResult(r.data);
    } catch (e) {
      setError(e.response?.data?.detail || t("aiContract.genError"));
    } finally {
      setGenerating(false);
    }
  };

  const downloadHtml = () => {
    if (!result?.html) return;
    const blob = new Blob([`<!DOCTYPE html><html lang="${i18n.language}"><head><meta charset="UTF-8"><title>${t("aiContract.docTitle", { label: result.preset_label })}</title><style>body{font-family:Georgia,serif;max-width:800px;margin:40px auto;line-height:1.6;padding:0 24px}h1{text-align:center;border-bottom:2px solid #333;padding-bottom:12px}h2{margin-top:30px;color:#333}table{border-collapse:collapse;width:100%}td,th{border:1px solid #ccc;padding:6px 10px}</style></head><body>${result.html}</body></html>`], { type: "text/html" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    const safeName = (result.employee_name || t("aiContract.defaultFileName")).replace(/\s+/g, "_");
    a.download = `Contrat_${result.preset_id}_${safeName}_${result.generated_at}.html`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const printPdf = () => {
    if (!result?.html) return;
    const w = window.open("", "_blank");
    if (!w) return;
    w.document.write(`<!DOCTYPE html><html lang="${i18n.language}"><head><meta charset="UTF-8"><title>${t("aiContract.docTitleShort")}</title><style>@media print{body{margin:20mm}}body{font-family:Georgia,serif;max-width:800px;margin:40px auto;line-height:1.6;padding:0 24px}h1{text-align:center;border-bottom:2px solid #333;padding-bottom:12px}h2{margin-top:30px;color:#333}table{border-collapse:collapse;width:100%}td,th{border:1px solid #ccc;padding:6px 10px}</style></head><body>${result.html}<script>window.onload=()=>window.print()</script></body></html>`);
    w.document.close();
  };

  return (
    <div className="p-6 space-y-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Sparkles className="h-6 w-6 text-primary" />
            {t("aiContract.title")}
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            {t("aiContract.subtitle")}
          </p>
        </div>
        <span className="text-xs bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-300 px-3 py-1 rounded-full font-medium">
          {t("aiContract.betaBadge")}
        </span>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* LEFT — Form */}
        <div className="space-y-5">
          <div>
            <label className="text-sm font-medium mb-2 block">{t("aiContract.step1")}</label>
            <div className="grid grid-cols-1 gap-2">
              {presets.map((p) => (
                <PresetCard key={p.id} preset={p} selected={presetId === p.id} onSelect={setPresetId} />
              ))}
            </div>
          </div>

          {currentPreset?.key_clauses?.length > 0 && (
            <div className="p-3 rounded-lg bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800">
              <p className="text-xs font-semibold text-blue-800 dark:text-blue-300 mb-1.5">{t("aiContract.autoClauses")}</p>
              <ul className="text-xs text-blue-700 dark:text-blue-400 space-y-0.5 list-disc list-inside">
                {currentPreset.key_clauses.map((c, i) => <li key={i}>{c}</li>)}
              </ul>
            </div>
          )}

          <div>
            <label className="text-sm font-medium mb-2 block">{t("aiContract.step2")}</label>
            <select className="w-full border rounded-md px-3 py-2 text-sm bg-background"
              value={employeeId} onChange={(e) => setEmployeeId(e.target.value)}>
              <option value="">{t("aiContract.noEmployee")}</option>
              {employees.map((e) => (
                <option key={e.id} value={e.id}>
                  {e.first_name} {e.last_name} · {e.job_title || e.registration_number}
                </option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-sm font-medium mb-2 block">{t("aiContract.salary")}</label>
              <input type="number" className="w-full border rounded-md px-3 py-2 text-sm bg-background"
                value={salary} onChange={(e) => setSalary(e.target.value)}
                placeholder={currentPreset?.currency || t("aiContract.amountPlaceholder")} />
            </div>
            <div>
              <label className="text-sm font-medium mb-2 block">{t("aiContract.startDate")}</label>
              <input type="date" className="w-full border rounded-md px-3 py-2 text-sm bg-background"
                value={startDate} onChange={(e) => setStartDate(e.target.value)} />
            </div>
          </div>

          {presetId?.includes("cdd") && (
            <div>
              <label className="text-sm font-medium mb-2 block">{t("aiContract.endDateCdd")}</label>
              <input type="date" className="w-full border rounded-md px-3 py-2 text-sm bg-background"
                value={endDate} onChange={(e) => setEndDate(e.target.value)} />
            </div>
          )}

          <div>
            <label className="text-sm font-medium mb-2 block">{t("aiContract.step3")}</label>
            <textarea className="w-full border rounded-md px-3 py-2 text-sm bg-background min-h-[100px]"
              value={instructions} onChange={(e) => setInstructions(e.target.value)}
              placeholder={t("aiContract.instructionsPlaceholder")} />
          </div>

          <button
            onClick={generate}
            disabled={generating || !presetId}
            className="w-full flex items-center justify-center gap-2 py-3 rounded-lg font-semibold text-white bg-primary hover:bg-primary/90 disabled:opacity-50 transition-colors"
          >
            {generating ? (
              <><Loader2 className="h-4 w-4 animate-spin" /> {t("aiContract.generating")}</>
            ) : (
              <><Wand2 className="h-4 w-4" /> {t("aiContract.generate")}</>
            )}
          </button>

          {error && (
            <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700 flex items-start gap-2">
              <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" />
              <span>{error}</span>
            </div>
          )}
        </div>

        {/* RIGHT — Output */}
        <div className="lg:col-span-2">
          {!result && !generating && (
            <div className="h-full min-h-[400px] flex flex-col items-center justify-center text-center p-12 border-2 border-dashed border-border rounded-xl bg-muted/20">
              <FileText className="h-12 w-12 text-muted-foreground/40 mb-4" />
              <p className="text-sm text-muted-foreground max-w-xs">
                {t("aiContract.emptyPre")}<strong>{t("aiContract.emptyStrong")}</strong>{t("aiContract.emptyPost")}
              </p>
            </div>
          )}

          {generating && (
            <div className="h-full min-h-[400px] flex flex-col items-center justify-center text-center p-12 border border-border rounded-xl bg-card">
              <Loader2 className="h-10 w-10 animate-spin text-primary mb-4" />
              <p className="font-medium">{t("aiContract.writing")}</p>
              <p className="text-xs text-muted-foreground mt-1">{t("aiContract.writingSub")}</p>
            </div>
          )}

          {result && (
            <div className="border border-border rounded-xl bg-card overflow-hidden">
              <div className="px-5 py-3 border-b border-border bg-muted/30 flex items-center justify-between flex-wrap gap-2">
                <div className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-green-500" />
                  <span className="text-sm font-medium">{result.preset_label}</span>
                  {result.employee_name && (
                    <span className="text-xs text-muted-foreground">· {result.employee_name}</span>
                  )}
                </div>
                <div className="flex gap-2">
                  <button onClick={generate} className="text-xs flex items-center gap-1 px-3 py-1.5 rounded-md border hover:bg-muted">
                    <RefreshCw className="h-3 w-3" /> {t("aiContract.regenerate")}
                  </button>
                  <button onClick={downloadHtml} className="text-xs flex items-center gap-1 px-3 py-1.5 rounded-md border hover:bg-muted">
                    <Download className="h-3 w-3" /> HTML
                  </button>
                  <button onClick={printPdf} className="text-xs flex items-center gap-1 px-3 py-1.5 rounded-md bg-primary text-primary-foreground hover:bg-primary/90">
                    <FileText className="h-3 w-3" /> {t("aiContract.printPdf")}
                  </button>
                </div>
              </div>
              {/* Render inside a sandboxed iframe: the generated contract is a full
                  HTML document with its own <style> (body/h1/table rules). Injecting
                  it inline leaked those global styles and broke the whole app layout. */}
              <iframe
                title={t("aiContract.docTitleShort")}
                className="w-full h-[70vh] border-0 bg-white"
                sandbox=""
                srcDoc={`<!DOCTYPE html><html lang="${i18n.language}"><head><meta charset="UTF-8"><meta name="viewport" content="width=device-width, initial-scale=1.0"><style>body{font-family:Georgia,serif;max-width:800px;margin:24px auto;line-height:1.6;padding:0 24px;color:#1a1a1a}h1{text-align:center;border-bottom:2px solid #333;padding-bottom:12px}h2{margin-top:30px;color:#333}table{border-collapse:collapse;width:100%}td,th{border:1px solid #ccc;padding:6px 10px}</style></head><body>${result.html}</body></html>`}
              />
              <div className="px-5 py-3 border-t border-border bg-amber-50 dark:bg-amber-950/30 text-xs text-amber-800 dark:text-amber-300 flex items-start gap-2">
                <AlertTriangle className="h-3.5 w-3.5 flex-shrink-0 mt-0.5" />
                <span>{result.disclaimer}</span>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
